import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useLocation } from "react-router-dom";
import api from "../../core/data/api";
import Loading from "../../core/common/loader/Loading";
import SelectCenters from '../Center/selectCenters';
import { FaPlay, FaPause, FaExpand, FaCog, FaVideo, FaExclamationTriangle, FaStop } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import "./VideoPlayer.css";
import { all_routes } from "../../feature-module/router/all_routes";
import SelectClassType from "../Users/SelectClassType";

const routes = all_routes;

const VideoPlayer: React.FC = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedCenter, setSelectedCenter] = useState<string>("");
  const [selectClass, setSelectClass] = useState<string>("");
  const [currentStreamKey, setCurrentStreamKey] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);
  const [centers, setCenters] = useState<{ ClassName: string; id: string }[]>([]);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [videoInfo, setVideoInfo] = useState<any>(null);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [popupMessage, setPopupMessage] = useState<string>("");

  // Stop only THIS user's playback (do not stop server-side stream).
  const stopCurrentStream = () => {
    try {
      // Stop HLS engine (if attached)
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      // Reset the video element to prevent endless buffering UI
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.removeAttribute("src");
        // Some browsers keep the last buffer until load() is called
        video.load();
      }
    } finally {
      setVideoUrl("");
      setIsPlaying(false);
      setCurrentStreamKey(null);
      localStorage.removeItem("stream");
    }
  };

  // Cleanup function when component unmounts or selections change
  useEffect(() => {
    return () => {
      stopCurrentStream();
    };
  }, []);

  // Stop stream when center/class/channel changes
  useEffect(() => {
    stopCurrentStream();
  }, [selectedCenter, selectClass, selectedClass]);

  useEffect(() => {
    const getCenter = async () => {
      try {
        setLoader(true);
        const res = await api.post("/api/mssql-procedure/execute/get", {
          procedureName: "ChannelsGetAll",
          parameters: [],
        });
        if (res.data?.centers && Array.isArray(res.data.centers)) {
          const filteredCenters = res.data.centers
            .filter((center: any) => {
              if (selectedCenter && selectedCenter != center?.CenterID) return false;
              if (!selectClass) return true;
              const classId = center?.ClassId ?? center?.ClassID ?? center?.Class ?? "";
              if (Array.isArray(classId)) return classId.includes(selectClass);
              return String(classId)
                .split(",")
                .map((s: string) => s.trim())
                .filter(Boolean)
                .includes(String(selectClass));
            })
            .map((center: any) => ({
              ClassName: center.camera,
              centerId: center.CenterID,
              id: center.ID,
            }));
          setCenters(filteredCenters);
        } else {
          setCenters([]);
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      } finally {
        setLoader(false);
      }
    };

    if (selectedCenter) {
      getCenter();
    } else {
      setCenters([]);
      setSelectedClass("");
    }
  }, [selectedCenter, selectClass]);

  useEffect(() => {
    if (selectedClass && selectedCenter) {
      const getCameraDetails = async () => {
        try {
          setLoader(true);
          const res = await api.post("/api/mssql-procedure/execute", {
            procedureName: "ChannelGetbyID",
            parameters: [
              { name: "camera", type: "VarChar", value: selectedClass },
              { name: "CenterID", type: "Int", value: selectedCenter },
              { name: "ClassId", type: "Int", value: selectClass },
            ],
          });

          const record = res.data?.record?.[0];
          if (record) {
            setFormData(record);
            setVideoInfo({
              name: record?.camera,
              ip: record?.domainIp,
              status: "Connecting..."
            });
          }
        } catch (error) {
          console.error("Error fetching camera details:", error);
        } finally {
          setLoader(false);
        }
      };
      getCameraDetails();
    }
  }, [selectedClass, selectedCenter]);


useEffect(() => {
  const prevPath = prevPathRef.current;
  const currentPath = location.pathname;
  console.log(currentPath,"currrent path",prevPath,"prev path")
  if (prevPath === "/liveCctv" && currentPath !== "/liveCctv") {
    stopCurrentStream();
  }

  prevPathRef.current = currentPath;
}, [location.pathname, currentStreamKey]); 
 useEffect(() => {
    const setupStream = async () => {
      if (!formData?.domainIp) return;

      // Stop any existing playback first (local only)
      stopCurrentStream();

      setVideoInfo((prev: any) => ({ ...prev, status: "Connecting..." }));

      const rtspUrl = formData?.rtspUrl
      let success = false;
      let lastError: any = null;
      let streamKey = null;

      for (let attempt = 1; attempt <= 4; attempt++) {
        try {
          const res = await api.post("/api/start-stream", { rtspUrl });
          if (res.data?.hlsUrl) {
            setVideoUrl(res.data.hlsUrl);
            // Store the stream key from backend response
            if (res.data.streamKey) {
              streamKey = res.data.streamKey;
              setCurrentStreamKey(streamKey);
            }
            setVideoInfo((prev: any) => ({
              ...prev,
              status: "Connected",
              error: null,
            }));
            success = true;
            break;
          } else {
            throw new Error(res.data?.message || "No HLS URL returned");
          }
        } catch (error: any) {
          lastError = error;
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      if (!success) {
        setVideoInfo((prev: any) => ({
          ...prev,
          status: "Error",
          error: lastError?.message || "Failed to start stream",
        }));
        // setPopupMessage("Stream error: " + (lastError?.message || "Stream failed"));
        // setShowPopup(true);
        setTimeout(() => setShowPopup(false), 5000);
      }
    };

    setupStream();
  }, [formData]);

  const handleSelect1 = async (value: string) => {
    // Stop current playback before changing center (local only)
    stopCurrentStream();
    setSelectedCenter(value);
    setSelectedClass("");
  };

  const handleSelect2 = async (value: any) => {
    // Stop current playback before changing class type (local only)
    stopCurrentStream();
    setSelectClass(value);
    setSelectedClass("");
  };

  const handleSelect = async (value: string) => {
    if (!selectedCenter) {
      setPopupMessage("Please select a center first");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }
    
    // Stop current playback before changing channel (local only)
    stopCurrentStream();
    
    setSelectedClass(value);
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(e => console.error("Play failed:", e));
      }
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      videoRef.current?.requestFullscreen().catch(e => console.error("Fullscreen error:", e));
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Handle manual stop stream button click
  const handleStopStream = async () => {
    setVideoInfo((prev: any) => ({ ...prev, status: "Stopped" }));
    stopCurrentStream();
    setPopupMessage("Stream stopped");
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3000);
  };

  useEffect(() => {
    let hls: Hls | null = null;

    if (videoUrl && videoRef.current) {
      const video = videoRef.current;

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);

      if (Hls.isSupported()) {
        hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(videoUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(e => console.error("Auto-play failed:", e));
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = videoUrl;
        video.addEventListener('loadedmetadata', () => {
          video.play().catch(e => console.error("Auto-play failed:", e));
        });
      }

      return () => {
        if (hls) {
          hls.destroy();
        }
        if (hlsRef.current === hls) {
          hlsRef.current = null;
        }
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      };
    }
  }, [videoUrl]);

  return (
    <div className="page-wrapper">
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="notification-popup"
          >
            <FaExclamationTriangle className="popup-icon" />
            <span>{popupMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="video-player-container">
        <div className="video-player-header">
          <h2>Live Camera Feed</h2>
          <div className="channel-selector">
            <div className="select-wrapper gap-2">
              <SelectCenters selectedCenter={selectedCenter} handleSelect={handleSelect1} />
              <SelectClassType selectedClass={selectClass} type="" selectedCenter={selectedCenter} handleSelect={handleSelect2} />
              <motion.select
                name="selectChannel"
                className="form-select"
                value={selectedClass}
                onChange={(e: any) => handleSelect(e.target.value)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={!selectedCenter || !selectClass}
              >
                <option value="">Select Channel</option>
                {centers.map((center) => (
                  <option key={center.id} value={center.ClassName}>
                    {center.ClassName}
                  </option>
                ))}
              </motion.select>
            </div>
          </div>
        </div>

        {loader ? (
          <div className="loading-overlay">
            <Loading />
            <p>Loading stream...</p>
          </div>
        ) : (
          <div className="video-content">
            {videoInfo && (
              <div className="video-info-card">
                <h3>{videoInfo.name}</h3>
                <div className="info-grid">
                  <div>
                    <span className="info-label">Status:</span>
                    <span className={`status-indicator ${videoInfo.status.toLowerCase()}`}>
                      {videoInfo.status}
                    </span>
                  </div>
                  <div>
                    <span className="info-label">IP Address:</span>
                    <span>{videoInfo.ip}</span>
                  </div>
                </div>
                {currentStreamKey && videoInfo.status === "Connected" && (
                  <div className="stop-stream-button-container">
                    <motion.button
                      onClick={handleStopStream}
                      className="stop-stream-button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FaStop /> Stop Stream
                    </motion.button>
                  </div>
                )}
              </div>
            )}

            {videoUrl ? (
              <div className="video-wrapper">
                <video ref={videoRef} autoPlay controls={true} className="video-element" />
                <div className="video-controls">
                  <motion.button onClick={togglePlayPause} className="control-button" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    {isPlaying ? <FaPause /> : <FaPlay />}
                  </motion.button>
                  <motion.button onClick={toggleFullscreen} className="control-button" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <FaExpand />
                  </motion.button>
                  {currentStreamKey && (
                    <motion.button 
                      onClick={handleStopStream} 
                      className="control-button stop" 
                      whileHover={{ scale: 1.1 }} 
                      whileTap={{ scale: 0.9 }}
                      title="Stop Stream"
                    >
                      <FaStop />
                    </motion.button>
                  )}
                </div>
              </div>
            ) : (
              <div className="video-placeholder">
                {selectedClass ? (
                  <div className="connecting-message">
                    <FaCog className="spinner-icon" />
                    <p>Establishing connection to {selectedClass}...</p>
                  </div>
                ) : (
                  <div className="select-message">
                    <FaVideo className="video-icon" />
                    <p>{selectedCenter ? "Please select a channel to view the live feed" : "Please select a center first"}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;